import React, {
  createContext, useCallback, useContext, useRef,
} from 'react';

interface TourContextValue {
  registerTour: (startTour: () => void) => void;
  unregisterTour: (startTour: () => void) => void;
  requestTour: () => boolean;
}

const TourContext = createContext<TourContextValue>({
  registerTour: () => {},
  unregisterTour: () => {},
  requestTour: () => false,
});

export const TourProvider = ({ children }: { children: React.ReactNode }) => {
  const startTourRef = useRef<(() => void) | null>(null);

  const registerTour = useCallback((startTour: () => void) => {
    startTourRef.current = startTour;
  }, []);

  // Only clears the ref if the unregistering component is still the registered one,
  // preventing a race condition when navigating between two tour pages where the new
  // page registers before the old page's cleanup runs.
  const unregisterTour = useCallback((startTour: () => void) => {
    if (startTourRef.current === startTour) {
      startTourRef.current = null;
    }
  }, []);

  const requestTour = useCallback(() => {
    if (startTourRef.current) {
      startTourRef.current();
      return true;
    }
    return false;
  }, []);

  return (
    <TourContext.Provider value={{
      registerTour,
      unregisterTour,
      requestTour,
    }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTourContext = () => useContext(TourContext);
